import { DateTime } from 'luxon';
import { MouseEvent, useState } from 'react';
import styled, { css } from 'styled-components';

import Text from '@/components/Text';
import { buildCalendar } from '@/utils/buildCalendar';
import { scrollBarStyles } from '@/utils/theme';

interface DayProps {
  $isOtherMonthDay?: boolean;
  $isToday?: boolean;
}

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const Box = styled.div`
  box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const BoxTop = styled.div`
  width: 100%;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-radius: 4px;
  grid-gap: 16px;
`;

export const Days = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-gap: 16px;
  margin-bottom: 9px;
`;

export const Day = styled(Text)<DayProps>`
  height: 40px;
  justify-content: center;
  display: flex;
  align-items: center;
  border-radius: 50%;
  width: 100%;
  ${({ $isOtherMonthDay }) =>
    $isOtherMonthDay &&
    css`
      opacity: 0 !important;
    `}
  ${({ $isToday }) =>
    $isToday &&
    css`
      opacity: 1;
      background: ${({ theme }) => theme.colors.error};
      color: ${({ theme }) => theme.colors.white};
    `};
`;

export const WeekDay = styled(Text)`
  text-align: center;
`;

const Title = styled(Text)`
  margin-bottom: 24px;
  width: 100%;
`;

export const Header = styled.div`
  padding: 16px 24px;
  margin-bottom: 16px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  border-bottom: solid 1px ${({ theme }) => theme.colors.grayscaleLight};
`;

export const MonthButtonHolder = styled.div`
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 24px 24px;
`;

export const MonthButton = styled.button`
  height: 24px;
  width: 24px;
  background: ${({ theme }) => theme.colors.grayscale};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity ease-out 0.2s;
  &:hover {
    opacity: 0.7;
  }
`;

const DropdownsHolder = styled.div`
  display: flex;
  gap: 8px;
`;

const Dropdown = styled.div`
  position: relative;
`;

const DropdownButton = styled.button`
  ${({ theme }) => css`
    font-family: ${theme.typography.button.fontFamily};
  `}
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  display: flex;
  align-items: center;
  height: 30px;
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;
  width: 96px;
  justify-content: center;
  transition: border-color ease-out 0.2s;
  &:hover {
    border-color: rgba(34, 34, 34, 0.25);
  }
  &:active {
    border-color: ${({ theme }) => theme.colors.gray};
  }
`;

const YearDropdownButton = styled(DropdownButton)`
  width: 60px;
`;

const DropdownSelections = styled.div`
  position: absolute;
  background-color: ${({ theme }) => theme.colors.white};
  top: calc(100% + 8px);
  left: 0;
  border-radius: 4px;
  padding: 2px 2px 2px 4px;
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  width: 124px;
  z-index: 1;
  max-height: 216px;
  overflow-y: auto;
  ${scrollBarStyles};
`;

const DropdownSelection = styled.button<{ $selected?: boolean }>`
  ${({ theme }) => css`
    font-family: ${theme.typography.button.fontFamily};
  `}
  width: 100%;
  font-size: 14px;
  line-height: 22px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray};
  transition: all ease-out 0.2s;
  margin: 4px 0;
  border-radius: 4px;
  &:hover {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
    color: ${({ theme }) => theme.colors.black};
  }
  ${({ theme, $selected }) =>
    $selected &&
    css`
      color: ${theme.colors.black};
      background-color: ${theme.colors.grayscaleLight};
    `}
`;

const Inner = styled.div`
  padding: 16px 24px;
`;

const Calendar = () => {
  const now = DateTime.now();
  const today = now.startOf('day');

  const [currentMonth, setCurrentMonth] = useState(now);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);

  const firstMothDay = currentMonth.startOf('month');
  const lastMonthDay = currentMonth.endOf('month');

  const calendar = buildCalendar(currentMonth);

  const getPreviousMonth = () => {
    return currentMonth.minus({ months: 1 });
  };

  const getNextMonth = () => {
    return currentMonth.plus({ months: 1 });
  };

  const getCurrentMonthName = () => {
    return currentMonth.toFormat('MMMM');
  };

  const getCurrentYear = () => {
    return currentMonth.toFormat('y');
  };

  return (
    <Box>
      <BoxTop>
        <Header>
          <Title $type="button">Calendar</Title>
          <DropdownsHolder>
            <Dropdown>
              <YearDropdownButton
                type="button"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  setShowYearDropdown((prev) => !prev);
                }}
              >
                {getCurrentYear()}
              </YearDropdownButton>
              {showYearDropdown && (
                <DropdownSelections>
                  {Array.from(Array(100).keys())
                    .map((key) => 4 + parseInt(now.toFormat('y')) - key)
                    .map((year, index) => (
                      <DropdownSelection
                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          setShowYearDropdown(false);
                          setCurrentMonth(currentMonth.set({ year: year }));
                        }}
                        type="button"
                        key={index}
                      >
                        {year}
                      </DropdownSelection>
                    ))}
                </DropdownSelections>
              )}
            </Dropdown>
            <Dropdown>
              <DropdownButton
                type="button"
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  setShowMonthDropdown((prev) => !prev);
                }}
              >
                {getCurrentMonthName()}
              </DropdownButton>
              {showMonthDropdown && (
                <DropdownSelections>
                  {months.map((month, index) => (
                    <DropdownSelection
                      onClick={(e: MouseEvent<HTMLButtonElement>) => {
                        setShowMonthDropdown(false);
                        e.stopPropagation();
                        setCurrentMonth(currentMonth.set({ month: index + 1 }));
                      }}
                      $selected={getCurrentMonthName() === month}
                      type="button"
                      key={index}
                    >
                      {month}
                    </DropdownSelection>
                  ))}
                </DropdownSelections>
              )}
            </Dropdown>
          </DropdownsHolder>
          <MonthButtonHolder>
            <MonthButton
              type="button"
              onClick={() => setCurrentMonth(getPreviousMonth())}
            >
              <svg
                width="7"
                height="12"
                viewBox="0 0 7 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.75729 1.75748L1.51465 6.00012L5.75729 10.2428"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </MonthButton>
            <MonthButton
              type="button"
              onClick={() => setCurrentMonth(getNextMonth())}
            >
              <svg
                width="7"
                height="12"
                viewBox="0 0 7 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.75736 1.75748L6 6.00012L1.75736 10.2428"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </MonthButton>
          </MonthButtonHolder>
        </Header>
        <Inner>
          <Days>
            <WeekDay $type="caption" $color="gray">
              Mo
            </WeekDay>
            <WeekDay $type="caption" $color="gray">
              Tu
            </WeekDay>
            <WeekDay $type="caption" $color="gray">
              We
            </WeekDay>
            <WeekDay $type="caption" $color="gray">
              Th
            </WeekDay>
            <WeekDay $type="caption" $color="gray">
              Fr
            </WeekDay>
            <WeekDay $type="caption" $color="gray">
              Sa
            </WeekDay>
            <WeekDay $type="caption" $color="gray">
              Su
            </WeekDay>
          </Days>
          <Grid>
            {calendar.days.map((day: DateTime, index: number) => {
              const isOtherMonthDay = day < firstMothDay || day > lastMonthDay;
              const isToday = day.toISODate() === today.toISODate();
              return (
                <Day
                  key={index}
                  $type="captionSemibold"
                  $isOtherMonthDay={isOtherMonthDay}
                  $isToday={isToday}
                >
                  {day.toFormat('d')}
                </Day>
              );
            })}
          </Grid>
        </Inner>
      </BoxTop>
    </Box>
  );
};

export default Calendar;
